/**
 * HEAREN BUILD 56 - Skeleton Loaders
 * 
 * Skeleton components for progressive loading UI.
 * Use these instead of empty states during data fetching.
 */

import React from 'react';

/**
 * Generic skeleton shimmer effect
 */
const shimmerClass = "animate-pulse bg-gradient-to-r from-gray-700 via-gray-600 to-gray-700 bg-[length:200%_100%]";

/**
 * Theme card skeleton
 */
export const ThemeCardSkeleton = ({ count = 4 }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="rounded-xl overflow-hidden">
        <div className={`h-24 md:h-32 ${shimmerClass}`} />
        <div className="p-3 bg-gray-800/50">
          <div className={`h-4 w-3/4 rounded ${shimmerClass}`} />
          <div className={`h-3 w-1/2 rounded mt-2 ${shimmerClass}`} />
        </div>
      </div>
    ))}
  </div>
);

/**
 * Track list skeleton
 */
export const TrackListSkeleton = ({ count = 6 }) => (
  <div className="space-y-3">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="flex items-center gap-4 p-3 rounded-lg bg-gray-800/30">
        <div className={`w-12 h-12 rounded-lg ${shimmerClass}`} />
        <div className="flex-1">
          <div className={`h-4 w-3/4 rounded ${shimmerClass}`} />
          <div className={`h-3 w-1/2 rounded mt-2 ${shimmerClass}`} />
        </div>
        <div className={`w-8 h-8 rounded-full ${shimmerClass}`} />
      </div>
    ))}
  </div>
);

/**
 * Single track skeleton
 */
export const TrackSkeleton = () => (
  <div className="flex items-center gap-4 p-3 rounded-lg bg-gray-800/30">
    <div className={`w-12 h-12 rounded-lg ${shimmerClass}`} />
    <div className="flex-1">
      <div className={`h-4 w-3/4 rounded ${shimmerClass}`} />
      <div className={`h-3 w-1/2 rounded mt-2 ${shimmerClass}`} />
    </div>
    <div className={`w-8 h-8 rounded-full ${shimmerClass}`} />
  </div>
);

/**
 * Channel/Category tabs skeleton
 */
export const TabsSkeleton = ({ count = 5 }) => (
  <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className={`h-10 w-24 rounded-full flex-shrink-0 ${shimmerClass}`} />
    ))}
  </div>
);

/**
 * Full page loading skeleton (themes + tracks)
 */
export const AudioPageSkeleton = () => (
  <div className="space-y-6 p-4">
    <TabsSkeleton count={5} />
    <ThemeCardSkeleton count={4} />
    <div className="mt-6">
      <div className={`h-6 w-32 rounded mb-4 ${shimmerClass}`} />
      <TrackListSkeleton count={6} />
    </div>
  </div>
);

/**
 * Inline loading indicator with text
 */
export const LoadingIndicator = ({ text = "Chargement...", className = "" }) => (
  <div className={`flex items-center justify-center gap-3 py-8 ${className}`}>
    <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
    <span className="text-gray-400 text-sm">{text}</span>
  </div>
);

/**
 * Small inline loader for buttons/actions
 */
export const InlineLoader = ({ size = 16 }) => (
  <div 
    className="border-2 border-current border-t-transparent rounded-full animate-spin" 
    style={{ width: size, height: size }}
  />
);

export default {
  ThemeCardSkeleton,
  TrackListSkeleton,
  TrackSkeleton,
  TabsSkeleton,
  AudioPageSkeleton,
  LoadingIndicator,
  InlineLoader
};
